import { useDispatch, useSelector } from 'react-redux';

import type { SpartacuxProductDiscoveryDomainState } from '@/domains/productDiscovery/productDiscovery.model';

import { searchInFacetValues as searchInFacetValuesAction } from '../../actions';
import { useFilterConfigs } from '../../hooks/useFiltersConfig';
import { useRefine } from '../../hooks/useRefine';
import type {
  QueryParamValue,
  RangeQueryParams,
} from '../../interfaces/algolia';
import { TechnicalFiltersOptions } from '../../interfaces/filterConfig';
import type { BoolValue, CheckboxValue, Facet } from '../interfaces/facets';
import { selectCustomFacet } from '../selectors/customFacets';
import { selectFacet } from '../selectors/facets';
import type {
  FacetValues,
  SearchInFacetValues,
  TUseFacet,
  TypedFacetValue,
} from './useFacet/useFacet.interfaces';
import { generateCheckboxUrlValue } from './useFacet/useFacet.utils';

/**
 * React hook which provide stored facet and two function to operate with him.
 * The first function is to refine facet value and the second is to search in
 * facet values.
 * @param facetName the name of the facet as string
 * @returns an object where there is `facet`, `searchInFacetValues` and
 * `refineFacetValue`
 * @example
 * const { facet, refineFacetValue } = useFacet<'checkbox'>('brand_name');
 * // {...}
 * refineFacetValue('MAKITA');
 */
export const useFacet = <FacetType extends keyof FacetValues>(
  facetName: string,
): TUseFacet<FacetType> => {
  const facet = useSelector<
    SpartacuxProductDiscoveryDomainState,
    Facet | undefined
  >(selectFacet(facetName));
  const customFacet = useSelector<
    SpartacuxProductDiscoveryDomainState,
    Facet | undefined
  >(selectCustomFacet(facetName));
  const dispatch = useDispatch();
  const refine = useRefine();
  const filterConfigs = useFilterConfigs();
  const filterConfig = filterConfigs.find(
    (config) => config.attributeName === facetName,
  );

  /**
   * This function allow to search in facet values by query as string
   * @param query your search string
   */
  const searchInFacetValues: SearchInFacetValues = (query) => {
    dispatch(searchInFacetValuesAction(facetName, query));
  };

  /**
   *
   * @param value it depend of FacetType generic. If FacetType is:
   * - `bool` => `boolean`.
   * - `categoryList` => `string`.
   * - `checkbox` => `string`.
   * - `color` => `string`.
   * - `radio` => `string`.
   * - `range` => `{ min?: number; max?: number }`.
   *
   * @param replace used to replace all filter values by new one
   */
  const refineFacetValue = (
    value:
      | (FacetType extends undefined ? string : TypedFacetValue<FacetType>)
      | undefined,
    replace = false,
  ) => {
    let nextURLValue: QueryParamValue = `${value}`;

    if (filterConfig?.urlKey && facet) {
      switch (facet.type) {
        case 'checkbox':
        case 'color':
          nextURLValue = replace
            ? (value as CheckboxValue)
            : generateCheckboxUrlValue(facet, value as CheckboxValue);
          break;

        case 'bool':
          nextURLValue = (value as BoolValue) ? '1' : undefined;
          break;

        case 'range':
          nextURLValue = value as RangeQueryParams;
          break;

        case 'categoryList':
        case 'menu':
        default:
          nextURLValue = value as string;
          break;
      }

      refine({
        [filterConfig.urlKey]: nextURLValue || undefined,
        page: undefined,
      });
    }
  };

  /**
   *
   * @param value it depend of FacetType generic. If FacetType is:
   * - `bool` => `boolean`.
   * - `categoryList` => `string`.
   * - `checkbox` => `string`.
   * - `color` => `string`.
   * - `custom` => `string`.
   * - `radio` => `string`.
   * - `range` => `{ min?: number; max?: number }`.
   */
  const removeFacetValue = (
    value:
      | (FacetType extends undefined ? string : TypedFacetValue<FacetType>)
      | undefined,
  ) => {
    let nextURLValue: QueryParamValue | undefined;
    let technicalFiltersOptions: TechnicalFiltersOptions | undefined;
    const currentFacet = facet || customFacet;

    if (filterConfig?.urlKey && currentFacet) {
      switch (currentFacet.type) {
        case 'checkbox':
        case 'color':
        case 'custom':
          nextURLValue = generateCheckboxUrlValue(
            currentFacet,
            value as CheckboxValue,
          );

          if (facetName === 'categories.last') {
            technicalFiltersOptions = {
              active: true,
              facetName,
              id: undefined,
            };
          }
          break;

        case 'bool':
        case 'range':
        case 'categoryList':
        case 'menu':
        default:
          break;
      }

      refine(
        {
          [filterConfig.urlKey]: nextURLValue || undefined,
          page: undefined,
        },
        undefined,
        undefined,
        technicalFiltersOptions,
      );
    }
  };

  return { facet, searchInFacetValues, refineFacetValue, removeFacetValue };
};
